/**
 * Center pane — server-rendered iframe at ``/api/decks/{id}/render``.
 *
 * Reload is keyed on the deck's ``cacheTag``: each successful save bumps
 * the tag, the parent re-renders, and the iframe ``src`` query string
 * changes, triggering a navigation in the iframe.
 *
 * Click-to-navigate (ADR-002 Phase A): the iframe is **same-origin**
 * (served under ``/api/decks/…`` and proxied through the dev server), so
 * the parent can reach ``contentWindow`` and drive the deck runtime. When
 * ``activeIdx`` changes — because a slide was selected/reordered in the
 * rail — we ask the runtime's ``window.__threadPresentation`` to
 * ``goToSlide(idx)``. The runtime may not be ready the instant the iframe
 * loads, so we poll briefly for it to finish booting; if it never appears
 * we fall back to a ``#slide=`` hash. The navigation is
 * re-applied after each ``cacheTag`` reload so the preview stays on the
 * selected slide across autosaves.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { deckRenderUrl } from "../api";

interface PreviewFrameProps {
  deckId: string;
  cacheTag: string;
  /**
   * Index of the slide selected in the rail. The preview navigates to
   * this slide via the deck runtime (or a ``#slide=`` hash fallback).
   * 0-based to match the rail; the hash fallback is 1-based.
   */
  activeIdx: number;
  /**
   * ADR-002 Phase C/D — when true, the parent has wired its in-place
   * edit / drag-and-drop hooks onto this iframe's document. Surfaced as a
   * ``data-edit-mode`` attribute so the affordance styling (edit cursor,
   * drag handles) can hang off it; the hooks themselves attach via the
   * iframe handed back through ``onReady``.
   */
  editMode?: boolean;
  /**
   * Called with the live iframe element on every ``load`` (i.e. on first
   * mount and after each ``cacheTag`` reload swaps the document). The
   * parent stashes it so its hooks can ``getIframe()`` and rebind their
   * document-level listeners onto the freshly-loaded ``contentDocument``.
   */
  onReady?: (iframe: HTMLIFrameElement) => void;
}

/** Minimal shape of the deck runtime we depend on (see ADR-002). */
interface ThreadPresentation {
  goToSlide?(idx: number): void;
  currentSlide?: number;
  ready?: boolean;
}

// How long to keep retrying for the runtime to appear after a load before
// using the hash fallback.
const RUNTIME_POLL_TIMEOUT_MS = 2000;
const RUNTIME_POLL_INTERVAL_MS = 50;

function getRuntime(win: Window | null | undefined): ThreadPresentation | undefined {
  const runtimeWindow = win as
    | (Window & { __threadPresentation?: ThreadPresentation })
    | null
    | undefined;
  return runtimeWindow?.__threadPresentation;
}

function runtimeCanNavigate(
  runtime: ThreadPresentation | undefined,
): runtime is ThreadPresentation & { goToSlide(idx: number): void } {
  return runtimeHasNavigationApi(runtime) && runtime.ready !== false;
}

function runtimeHasNavigationApi(
  runtime: ThreadPresentation | undefined,
): runtime is ThreadPresentation & { goToSlide(idx: number): void } {
  return typeof runtime?.goToSlide === "function";
}

export function PreviewFrame({
  deckId,
  cacheTag,
  activeIdx,
  editMode = false,
  onReady,
}: PreviewFrameProps) {
  // ``edit`` = the dedicated plain, runtime-free layout (real, clickable, sized
  // slide text, each slide in its own ``<section data-slide-idx>``); ``desktop``
  // = the 3D presentation view (text collapsed to 0×0 behind the substrate
  // canvas, so not directly editable — ADR-002 / FXC-8818). Toggling edit mode
  // swaps which the iframe loads, so it's part of the load key: a change
  // remounts the iframe and re-fires ``onReady`` so the edit/DnD hooks rebind.
  const layout: "desktop" | "mobile" | "edit" = editMode ? "edit" : "desktop";
  const ref = useRef<HTMLIFrameElement | null>(null);
  const [loadKey, setLoadKey] = useState(`${cacheTag}|${layout}`);
  // Tracks whether the *current* iframe (identified by ``loadKey``) has
  // fired its ``load`` event. Reset whenever we swap the iframe out.
  const [loaded, setLoaded] = useState(false);

  // Hold ``onReady`` in a ref so the iframe's ``onLoad`` closure doesn't
  // have to be re-created when the parent passes a fresh callback — the
  // iframe element identity stays stable across parent re-renders.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // Bump the load key when cacheTag OR the layout (edit/present) changes, so
  // the iframe reloads with the right render URL.
  useEffect(() => {
    setLoadKey(`${cacheTag}|${layout}`);
    setLoaded(false);
  }, [cacheTag, layout]);

  // Reach into the same-origin iframe and navigate to ``activeIdx`` via
  // the deck runtime, falling back to a ``#slide=`` hash. Returns true
  // once a navigation was applied (runtime *or* fallback) so the poller
  // can stop. Swallows the cross-origin ``SecurityError`` that would be
  // thrown if the assumption in ADR-002 ever breaks for a standalone
  // host — in that case the hash fallback on ``src`` still works.
  const navigateToActive = useCallback((): boolean => {
    const iframe = ref.current;
    if (!iframe) return false;
    let win: Window | null = null;
    try {
      win = iframe.contentWindow;
    } catch {
      win = null;
    }
    if (win) {
      try {
        const runtime = getRuntime(win);
        if (runtimeCanNavigate(runtime)) {
          runtime.goToSlide(activeIdx);
          return true;
        }
      } catch {
        // Cross-origin or runtime threw — fall through to the hash.
      }
    }
    // Fallback: set a ``#slide=`` hash (1-based) for compatible hosts.
    // Only rewrite the hash when it differs to avoid spurious history
    // churn / reload loops.
    const hash = `#slide=${activeIdx + 1}`;
    try {
      if (win && win.location.hash !== hash) {
        win.location.hash = hash;
        return true;
      }
      if (win) return true; // already on the right slide
    } catch {
      // Cross-origin: fall back to mutating the element's ``src`` hash.
    }
    const base = deckRenderUrl(deckId, cacheTag);
    const desired = `${base}${hash}`;
    if (iframe.src !== desired) iframe.src = desired;
    return true;
  }, [activeIdx, cacheTag, deckId]);

  // Drive the preview to the selected slide. Runs on initial load, on
  // every ``activeIdx`` change, and after each ``cacheTag`` reload (via
  // ``loadKey``/``loaded``). Polls briefly for the runtime to come up.
  useEffect(() => {
    // The plain edit layout (mobile) has no ``__threadPresentation`` runtime to
    // drive — slide selection there is by scrolling (handled separately), so
    // skip the runtime/hash navigation entirely while editing.
    if (!loaded || editMode) return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    // First attempt synchronously — covers the common case where the
    // runtime is already up by the time ``load`` fired.
    const win = (() => {
      try {
        return ref.current?.contentWindow ?? null;
      } catch {
        return null;
      }
    })();
    const runtimeReady = (() => {
      try {
        return runtimeCanNavigate(getRuntime(win));
      } catch {
        return false;
      }
    })();

    if (runtimeReady) {
      navigateToActive();
      return;
    }

    // Runtime not up yet — poll until it is, or time out and fall back.
    // If the API exists but is still booting, keep polling so late readiness
    // still drives the selected slide.
    const startedAt = Date.now();
    intervalId = setInterval(() => {
      if (cancelled) return;
      let ready = false;
      let runtimeHasApi = false;
      try {
        const runtime = getRuntime(ref.current?.contentWindow);
        runtimeHasApi = runtimeHasNavigationApi(runtime);
        ready = runtimeCanNavigate(runtime);
      } catch {
        ready = false;
        runtimeHasApi = false;
      }
      if (ready) {
        if (intervalId !== undefined) clearInterval(intervalId);
        navigateToActive();
        return;
      }
      if (
        !runtimeHasApi &&
        Date.now() - startedAt >= RUNTIME_POLL_TIMEOUT_MS
      ) {
        if (intervalId !== undefined) clearInterval(intervalId);
        // Give up on the runtime and use the hash fallback.
        navigateToActive();
      }
    }, RUNTIME_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId !== undefined) clearInterval(intervalId);
    };
  }, [loaded, loadKey, activeIdx, navigateToActive, editMode]);

  // Plain-layout slide navigation (ADR-002 Phase A equivalent — FXC-8818 task
  // 4). The ``edit`` layout has no ``__threadPresentation`` runtime to
  // ``goToSlide``, so selecting/reordering a slide in the rail scrolls the
  // iframe document to that slide's ``<section data-slide-idx>``. Runs on load
  // and every ``activeIdx`` change while editing; the runtime path above bails
  // in edit mode, so exactly one of the two drives the preview.
  useEffect(() => {
    if (!loaded || !editMode) return;
    let doc: Document | null = null;
    try {
      doc = ref.current?.contentDocument ?? null;
    } catch {
      doc = null;
    }
    const section = doc?.querySelector<HTMLElement>(
      `[data-slide-idx="${activeIdx}"]`,
    );
    // Instant (not smooth) so the jump is reliable everywhere — a rail click is
    // a deliberate "go to that slide", and smooth scrolling is a no-op under
    // prefers-reduced-motion and some embedded/headless webviews. ``scrollIntoView``
    // is unimplemented in jsdom — optional-call it so unit tests don't throw.
    section?.scrollIntoView?.({ block: "start" });
  }, [loaded, loadKey, activeIdx, editMode]);

  return (
    <section
      style={wrapperStyle}
      data-testid="preview-frame-wrapper"
      data-edit-mode={editMode ? "true" : "false"}
    >
      <header style={headerStyle}>
        <span style={{ color: "var(--gray-9)", fontSize: "0.85em" }}>
          Preview
        </span>
        <span style={{ color: "var(--gray-9)", fontSize: "0.75em" }}>
          cacheTag = <code>{cacheTag}</code>
        </span>
      </header>
      <iframe
        ref={ref}
        key={loadKey}
        title="Deck preview"
        src={deckRenderUrl(deckId, cacheTag, layout)}
        style={iframeStyle}
        data-testid="deck-preview-iframe"
        onLoad={() => {
          setLoaded(true);
          // Hand the live element to the parent so its edit/DnD hooks can
          // (re)bind onto the freshly-loaded ``contentDocument`` — fires on
          // first mount and after every ``cacheTag`` reload.
          if (ref.current) onReadyRef.current?.(ref.current);
        }}
      />
    </section>
  );
}

const wrapperStyle: React.CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  background: "var(--gray-2)",
  borderRight: "1px solid var(--gray-5)",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.5rem 0.75rem",
  borderBottom: "1px solid var(--gray-5)",
  background: "var(--gray-1)",
};

const iframeStyle: React.CSSProperties = {
  flex: 1,
  width: "100%",
  border: "none",
  background: "white",
};
